import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Eye, FileText, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useSetVerificationStatus, useVerificationQueue, type AgencyMember } from "@/hooks/platform/useAgency";
import { LoadingState, ErrorState, EmptyState } from "@/components/dashboard/common/PageShell";
import DocumentViewerDialog, { type VerificationDoc } from "@/components/admin/DocumentViewerDialog";

const statusStyle: Record<string, string> = {
  verified: "bg-emerald-600",
  in_review: "bg-amber-600",
  required: "bg-red-600",
  unverified: "bg-gray-700",
};

const VerificationReview = ({ filterType }: { filterType?: "creator" | "venue" }) => {
  const { data: rows = [], isLoading, error, refetch } = useVerificationQueue();
  const setStatus = useSetVerificationStatus();
  const [search, setSearch] = useState("");
  const [doc, setDoc] = useState<VerificationDoc | null>(null);

  const members = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows
      .filter((m) => (filterType ? m.user_type === filterType : true))
      .filter((m) =>
        !term
          ? true
          : [m.username, m.full_name, m.business_name, m.portal_id].some((v) => (v ?? "").toLowerCase().includes(term)),
      );
  }, [rows, filterType, search]);

  const docsOf = (m: AgencyMember): VerificationDoc[] =>
    Object.entries(m.verification_documents ?? {}).map(([key, d]) => ({ key, label: d.label, name: d.name, url: d.url }));

  const act = (m: AgencyMember, status: "verified" | "required") =>
    setStatus.mutate(
      { id: m.id, status },
      {
        onSuccess: () => toast.success(status === "verified" ? "Account verified" : "Sent back for more documents"),
        onError: (e) => toast.error(e instanceof Error ? e.message : "Could not update verification"),
      },
    );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Verification &amp; documents</h2>
          <p className="text-sm text-gray-400">
            Review certified documents uploaded by {filterType === "venue" ? "venues" : filterType === "creator" ? "creators" : "members"} and approve access.
          </p>
        </div>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or portal ID"
          className="w-64 bg-zinc-950 border-zinc-800 text-white"
        />
      </div>

      {isLoading ? (
        <LoadingState label="Loading verification queue…" />
      ) : error ? (
        <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
      ) : members.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="Nothing to review" description="Submitted verifications will appear here." />
      ) : (
        <div className="space-y-3">
          {members.map((m) => {
            const docs = docsOf(m);
            return (
              <Card key={m.id} className="bg-white/5 border-white/20">
                <CardHeader className="pb-2">
                  <CardTitle className="flex flex-wrap items-center gap-3 text-base text-white">
                    <span>{m.business_name || m.full_name || m.username}</span>
                    <Badge className={`${statusStyle[m.verification_status ?? "unverified"] ?? "bg-gray-700"} border-0 capitalize`}>
                      {(m.verification_status ?? "unverified").replace("_", " ")}
                    </Badge>
                    <span className="font-mono text-xs text-gray-400">{m.portal_id ?? "—"}</span>
                    <span className="text-xs text-gray-500 capitalize">{m.user_type}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-2 sm:grid-cols-2 text-xs text-gray-400">
                    <p>Registration: {m.business_registration || "—"}</p>
                    <p>Phone: {m.phone || "—"}</p>
                    <p>Address: {[m.address, m.city, m.country].filter(Boolean).join(", ") || "—"}</p>
                    <p>
                      Submitted:{" "}
                      {m.verification_submitted_at ? format(new Date(m.verification_submitted_at), "d MMM yyyy HH:mm") : "—"}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {docs.length === 0 ? (
                      <p className="text-xs text-gray-500">No documents uploaded.</p>
                    ) : (
                      docs.map((d) => (
                        <Button
                          key={d.key}
                          size="sm"
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/10 gap-2"
                          onClick={() => setDoc(d)}
                        >
                          <FileText className="h-3.5 w-3.5" />
                          <span className="max-w-[220px] truncate">{d.label}</span>
                          <Eye className="h-3.5 w-3.5 text-gray-400" />
                        </Button>
                      ))
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" className="gap-1" disabled={setStatus.isPending} onClick={() => act(m, "verified")}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-white/20 text-red-400 gap-1"
                      disabled={setStatus.isPending}
                      onClick={() => act(m, "required")}
                    >
                      <XCircle className="h-3.5 w-3.5" /> Request more documents
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <DocumentViewerDialog doc={doc} open={!!doc} onOpenChange={(v) => !v && setDoc(null)} />
    </div>
  );
};

export default VerificationReview;
